import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileDown } from "lucide-react";
import { useFileViewer } from "@/components/FileViewer";

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm text-slate-900">{value || "—"}</div>
  </div>
);

const Section = ({ title, children, testid }) => (
  <section className="mt-5" data-testid={testid}>
    <h2 className="border-b border-slate-300 pb-1 text-xs font-bold uppercase tracking-wider text-[#134e4a]">{title}</h2>
    <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">{children}</div>
  </section>
);

export default function CaseReport() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [logo, setLogo] = useState(null);
  const [photos, setPhotos] = useState({});
  const { openFile, viewerElement } = useFileViewer();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}/report`);
      setReport(data);
      if (data.lab.has_logo) {
        api.get("/lab/logo", { responseType: "blob" })
          .then((r) => setLogo(URL.createObjectURL(r.data))).catch(() => {});
      }
      (data.images || []).forEach((img) => {
        const url = img.annotated ? `/attachments/${img.id}/annotated` : `/attachments/${img.id}/download`;
        api.get(url, { responseType: "blob" })
          .then((r) => setPhotos((p) => ({ ...p, [img.id]: URL.createObjectURL(r.data) })))
          .catch(() => {});
      });
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openPdf = () => openFile(`/cases/${id}/report.pdf`, {
    filename: `${report?.case?.accession_number || "report"}.pdf`,
    title: `${report?.case?.accession_number || "Case"} — final report`,
  });

  if (!report) return <div className="text-sm text-slate-500" data-testid="report-loading">Loading report…</div>;
  const { case: c, lab, materials, sendouts, amendments } = report;
  const isFinal = ["Final", "Amended"].includes(c.report_status);

  return (
    <div className="mx-auto w-full max-w-[8.5in]" data-testid="case-report-page">
      <div className="mb-4 flex flex-wrap items-center gap-2 no-print">        <Link to={`/cases/${id}`} className="inline-flex h-11 items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline" data-testid="report-back-link">
          <ArrowLeft size={15} /> Back to case
        </Link>
        <Button onClick={() => window.print()} data-testid="report-print-button" className="ml-auto h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
          <Printer size={15} className="mr-2" /> Print
        </Button>
        <Button variant="outline" onClick={openPdf} data-testid="report-pdf-button" className="h-11">
          <FileDown size={15} className="mr-2" /> Download PDF
        </Button>
      </div>

      <article className="w-full overflow-x-auto border border-slate-300 bg-white p-4 print-sheet sm:p-8" data-testid="report-sheet">
        <header className="flex flex-wrap items-start gap-4 border-b border-slate-300 pb-3">
          {logo && <img src={logo} alt="Lab logo" className="h-14 w-auto object-contain" data-testid="report-logo" />}
          <div>
            <div className="font-display text-lg font-bold text-slate-900">{lab.name}</div>
            {lab.address && <div className="text-xs text-slate-600">{lab.address}</div>}
            {lab.contact_email && <div className="text-xs text-slate-600">{lab.contact_email}</div>}
          </div>
          <img src="/pathqity-logo.png" alt="PathQity" className="ml-auto h-11 w-auto object-contain"
               onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="report-brand-logo" />
        </header>

        <h1 className="mt-4 text-center font-display text-base font-bold uppercase tracking-wide text-[#134e4a]" data-testid="report-title">
          {c.report_status === "Amended" ? "Amended Surgical Pathology Report" : "Surgical Pathology Report"}
        </h1>
        {!isFinal && (
          <p className="mt-1 text-center text-sm font-bold uppercase tracking-widest text-red-700" data-testid="report-draft-banner">
            Draft — not a final report
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 border-b border-slate-200 pb-3 min-[420px]:grid-cols-2 sm:grid-cols-3" data-testid="report-patient-block">
          <Field label="Accession" value={<span className="font-mono font-semibold">{c.accession_number}</span>} />
          <Field label="Patient" value={c.patient_name} />
          <Field label="MRN" value={c.patient_mrn} />
          <Field label="Date of birth" value={c.patient_dob} />
          <Field label="Ordering physician" value={c.ordering_physician} />
          <Field label="Origin" value={c.origin_facility} />
          <Field label="Received" value={fmtDateTime(c.received_at)} />
          <Field label="Reported" value={c.report_signed_at ? fmtDateTime(c.report_signed_at) : "—"} />
          <Field label="Pathologist" value={c.pathologist_name} />
        </div>

        <Section title="Specimen submitted" testid="report-specimen">
          {c.specimen_class || "Surgical"}{c.cytology_type ? ` (${c.cytology_type})` : ""} — {c.specimen_type || "—"} / {c.specimen_site || "—"}
          {c.priority === "STAT" ? "   ·   PRIORITY: STAT" : ""}
        </Section>

        <Section title="Gross description" testid="report-gross">{c.gross_description || "—"}</Section>

        <Section title="Diagnosis" testid="report-diagnosis">
          <span className="font-semibold">{c.diagnosis || "—"}</span>
        </Section>

        {(c.comments || "").trim() && <Section title="Comment" testid="report-comment">{c.comments}</Section>}

        {report.synoptic && report.synoptic.rows.length > 0 && (
          <Section title={`Synoptic report — ${report.synoptic.template_name}`} testid="report-synoptic">
            <table className="w-full text-xs">
              <tbody>
                {report.synoptic.rows.map((r) => (
                  <tr key={r.label} className="border-b border-slate-100">
                    <td className="w-1/3 py-1 pr-3 align-top text-slate-500">{r.label}</td>
                    <td className="py-1 align-top font-medium text-slate-900">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {materials.length > 0 && (
          <Section title="Materials examined" testid="report-materials">
            <table className="w-full text-xs">
              <tbody>
                {materials.map((m) => (
                  <tr key={m.block} className="border-b border-slate-100">
                    <td className="w-24 py-1 font-mono font-semibold">{m.block}</td>
                    <td className="py-1">{m.slides.join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-1 text-xs text-slate-500">{report.block_count} block(s), {report.slide_count} slide(s) on file.</div>
          </Section>
        )}

        {sendouts.length > 0 && (
          <Section title="Referred / send-out testing" testid="report-sendouts">
            {sendouts.map((s, i) => (
              <div key={`${s.destination_lab}-${i}`}>
                {s.test_requested} → {s.destination_lab} ({fmtDateTime(s.date_sent)}) — {s.sendout_status}
                {s.results_summary ? `: ${s.results_summary}` : ""}
              </div>
            ))}
          </Section>
        )}

        {(report.cpt_items || []).length > 0 && (
          <Section title="Billing codes" testid="report-cpt">
            <table className="w-full text-xs">
              <tbody>
                {report.cpt_items.map((i) => (
                  <tr key={i.code} className="border-b border-slate-100">
                    <td className="w-20 py-1 font-mono font-semibold">{i.code}</td>
                    <td className="py-1">{i.description || "—"}</td>
                    <td className="w-16 py-1 text-right">×{i.units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.cpt_note && <div className="mt-1 text-xs text-slate-500">{report.cpt_note}</div>}
          </Section>
        )}

        {(report.images || []).length > 0 && (
          <Section title="Gross photographs" testid="report-photos">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {report.images.map((img) => (
                <figure key={img.id} data-testid={`report-photo-${img.id}`}>
                  {photos[img.id]
                    ? <img src={photos[img.id]} alt={img.caption || img.kind} className="max-h-52 w-full border border-slate-200 object-contain" />
                    : <div className="flex h-32 items-center justify-center border border-dashed border-slate-300 text-xs text-slate-400">Loading image…</div>}
                  <figcaption className="mt-1 text-xs text-slate-600">
                    {img.caption || img.kind}{img.annotated ? " (annotated)" : ""}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Section>
        )}

        {amendments.length > 0 && (
          <Section title="Amendment history" testid="report-amendments">
            {amendments.map((a) => (
              <div key={a.version} className="mb-2 text-xs text-slate-600">
                <div>v{a.version} — {fmtDateTime(a.at)} by {a.by}: {a.reason}</div>
                <div className="text-slate-500">Previous diagnosis: {a.previous_diagnosis || "—"}</div>
              </div>
            ))}
          </Section>
        )}

        {(report.distributions || []).length > 0 && (
          <Section title="Report distribution" testid="report-distributions">
            {report.distributions.map((d) => (
              <div key={d.id} className="mb-1 text-xs text-slate-600">
                {d.report_kind} v{d.report_version} — {d.status}
                {d.destination ? ` via ${d.destination}` : ""}
                {d.recipient ? ` to ${d.recipient}` : ""}
                {d.delivered_at ? ` on ${fmtDateTime(d.delivered_at)}` : ""}
                {d.confirmed_by ? ` (confirmed by ${d.confirmed_by})` : ""}
                {d.read_back_confirmed ? " · read-back confirmed" : ""}
              </div>
            ))}
            <Link to="/accreditation/distribution" className="text-xs font-semibold text-[#134e4a] hover:underline no-print">
              Open the distribution log →
            </Link>
          </Section>
        )}

        <footer className="mt-8 border-t border-slate-300 pt-3 text-sm" data-testid="report-signature">
          {c.report_signed_at ? (
            <div className="font-semibold text-slate-900">
              Electronically signed by {c.report_signed_by} — {fmtDateTime(c.report_signed_at)}
              {Number(c.report_version || 1) > 1 ? ` (report v${c.report_version})` : ""}
            </div>
          ) : (
            <div className="text-slate-600">Not yet signed out — draft copy.</div>
          )}
          {lab.footer && <div className="mt-1 text-xs text-slate-500">{lab.footer}</div>}
          <div className="mt-1 text-xs text-slate-400">Generated by PathTraX · PathQity · {c.accession_number}</div>
        </footer>
      </article>
      {viewerElement}
    </div>
  );
}
